import type { FC } from 'react';

import { useConfig } from '@packages/utilities';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { SearchApiResponse, SearchSeoApiResponse } from '../../api/rest/types';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';

import { ProductListPageLayout } from '../ProductListPageLayout';
import { SearchResultHeader } from '../SearchResultHeader';
import { SearchFeedback } from '../SearchFeedback';
import { ProductListSeo } from '../ProductListSeo';
import { Recommendations } from '../Recommendations';
import { SearchResultCategoryFilter } from '../SearchResultCategoryFilter';

export const SearchResultPageDefaultContentFragment = graphql(/* GraphQL */ `
  fragment SearchResultPageDefaultContentFragment on SuccessfulSearchResult {
    ...SearchResultHeaderFragment
  }
`);

export type SearchResultPageDefaultContentProps = {
  maskedData: FragmentType<typeof SearchResultPageDefaultContentFragment>;
  apiResponse: SearchApiResponse;
  topLevelCategories?: string[];
  seoResponse?: SearchSeoApiResponse;
};

/**
 * SearchResultPageDefaultContent component description displayed in storybook
 * */
export const SearchResultPageDefaultContent: FC<SearchResultPageDefaultContentProps> = ({
  maskedData,
  apiResponse,
  topLevelCategories,
  seoResponse,
}) => {
  const { feedback } = useConfig();
  const originalQuery = useOriginalSearchQuery();

  const data = getFragmentData(SearchResultPageDefaultContentFragment, maskedData);

  // TODO check if `navigation` always exists
  const { categoryNavigation = [] } = apiResponse.searchresult.result.navigation;

  const seoInformationExists = seoResponse && Object.keys(seoResponse).length > 0;

  return (
    <ProductListPageLayout
      apiResponse={apiResponse}
      topLevelCategories={topLevelCategories}
      pageType="serp"
      aboveMainContentComponent={
        <SearchResultHeader maskedData={data} originalQuery={originalQuery} />
      }
      belowProductsComponent={
        <>
          <Recommendations />
          {feedback.enabled && <SearchFeedback />}
          {seoInformationExists && (
            <ProductListSeo
              data={{
                ...seoResponse,
                relationlinks: undefined,
                similarlinks: undefined,
                filterlinks: undefined,
              }} // Only toplinks (shopping-tips) should be displayed in SERP
            />
          )}
        </>
      }
      leftSidebarDesktopComponent={<SearchResultCategoryFilter categories={categoryNavigation} />}
      renderCategoryDialogContent={(closeDialog) => (
        <SearchResultCategoryFilter categories={categoryNavigation} onNavigate={closeDialog} />
      )}
    />
  );
};
